/**
 * 開始演出: ピースのフライイン（TK-037）。
 *
 * ステージ開始時、まず盤面の地だけが見え、シャッフル済みのピースが画面外から飛来して
 * 初期配置に収まる。**経路曲線と運動則は直交**しており、ステージごとにシードで選んだ
 * 組み合わせが決まる（ユーザー指定。「組み合わせられるのでかなり色々できる」）。
 *
 * 経路曲線（mathcurve のカタログ `Geometry-Presentation-Engine-pre/mathcurve` から採用）:
 * - `line` — 直線（droite）
 * - `arc` — 中間で横へ膨らむ弓なりの経路（放物線的な swoop。着地は斜めに入る）
 * - `clothoid` — 開始側で曲がり、着地へ向けて真っ直ぐ締まる遷移曲線
 *   （クロソイド / コルニュ螺旋の「曲率が徐々に変わる進入」の性質を横偏差 s²(1−s) で近似）
 *
 * 運動則:
 * - `linear` — 等速直線運動
 * - `decel` — 等加速度運動（減速して着地。残距離 ∝ (1−p)²）
 * - `adsorb` — 吸着（追跡曲線 / poursuite の趣。現在位置と定位置の間の内分点へ指数的に
 *   吸い寄せられ、十分近づいたら定位置に到達 = スナップ）
 *
 * その他の要件:
 * - 進入方角はステージごとに 360° から決定的に選び、各ピースは ±30° のジッター
 * - **進入方角から遠い側のピースが先に**収まる（カタカタカタッと順に嵌る。後続の経路上に
 *   着地済みピースが現れない配置でもある）
 * - 画面外の初期距離は、最初に収まるピースと最後のピースで 2 倍以上の隔たり
 * - 飛行中のピース同士は重ならない。軌道関数そのものを時刻サンプリングで検査し、
 *   衝突する候補はジッター縮小・反転、距離ブースト（速度だけ上がる）、経路の直線化で修復する
 *
 * 描画は rAF 駆動（`runIntro`）。CSS transition では曲線経路と吸着が表現できないため、
 * 計画とランタイムが同じ軌道関数 `introOffsetAt` を共有する（検証済みの軌道がそのまま動く）。
 */

import { col, row } from '../core/grid';
import { mulberry32 } from '../core/shuffle';
import type { Placement } from '../core/types';
import { pieceSize } from './piece-view';

/** 最初に収まるピースの飛行時間（ms）。 */
const INTRO_FIRST_ARRIVAL_MS = 700;

/**
 * 全ピースの到着が詰め込まれる時間幅（ms）。到着時刻は距離ではなくこの窓で決め、
 * 「ざわざわっと連続して着地する」感じを作る（ユーザー指定。8:8 では約 7ms 間隔）。
 */
const INTRO_ARRIVAL_WINDOW_MS = 450;

/** 演出全体の公称長（ms）。実測は計画の最長 duration。 */
export const INTRO_TOTAL_MS = INTRO_FIRST_ARRIVAL_MS + INTRO_ARRIVAL_WINDOW_MS;

/** 最初に収まるピースの画面外距離（論理 px）。盤面の対角 600 + 余白。 */
const INTRO_BASE_DISTANCE = 720;

/**
 * 最初と最後のピースの距離比の下限。当初の「2 倍以上」は到着の間延びを生んだため、
 * ユーザー指定で軽減した（到着時刻は距離と独立に窓で決まる）。
 */
const INTRO_MIN_DISTANCE_RATIO = 1.4;

/** ジッターの振れ幅（度）。 */
const INTRO_JITTER_DEG = 30;

/** 衝突検査の時刻刻み（ms）。 */
const SIM_STEP_MS = 24;

/**
 * 衝突判定の許容めり込み（論理 px）。着地間際の数 px の残オフセットで、ちょうど接する
 * 隣接ピースが「重なり」と誤判定されるのを避ける。視認できない量に留める。
 */
const SIM_EPS = 3;

/** 吸着（adsorb）の減衰の強さ。exp(-6) ≈ 0.25% まで寄ってからスナップする。 */
const ADSORB_DECAY = 6;

export type IntroMotion = 'linear' | 'decel' | 'adsorb';
export type IntroPath = 'line' | 'arc' | 'clothoid';

/** ピース 1 枚の飛行計画。`pieces[piece]` で引く。 */
export interface IntroPiecePlan {
  /** 開始オフセット（論理 px。着地位置に加算すると開始位置になる）。 */
  readonly dx: number;
  readonly dy: number;
  /** 飛行時間（ms）。全ピース同時に発進し、これが収まる順を決める。 */
  readonly duration: number;
  /** 経路の横方向の膨らみ（論理 px。符号は膨らむ側）。line では 0。 */
  readonly bulge: number;
}

/** ステージ 1 回ぶんの開始演出の計画。 */
export interface IntroPlan {
  /** 進入方角（ラジアン。ピースが来る側を指す単位ベクトルの角度）。 */
  readonly angle: number;
  readonly motion: IntroMotion;
  readonly path: IntroPath;
  readonly pieces: readonly IntroPiecePlan[];
  /** 演出全体の長さ（ms）。 */
  readonly total: number;
  /** 秘文字レイヤー退場の移動ベクトル（論理 px）。進入方角と同じ側へフレームアウトする。 */
  readonly exit: { readonly x: number; readonly y: number };
}

/** 時刻 `t` における残距離（経路パラメータ）の割合。1 = 開始位置、0 = 着地。 */
export function remainingRatio(motion: IntroMotion, t: number, duration: number): number {
  const p = Math.min(1, Math.max(0, t / duration));
  if (p >= 1) return 0;
  if (motion === 'linear') return 1 - p;
  if (motion === 'decel') return (1 - p) ** 2;
  // adsorb: 指数減衰を [0,1] に正規化し、p=1 でちょうど 0（= スナップして定位置に到達）。
  const e = Math.exp(-ADSORB_DECAY);
  return (Math.exp(-ADSORB_DECAY * p) - e) / (1 - e);
}

/** 経路曲線の横方向偏差の係数（残割合 `s` で 0〜1）。開始 (s=1) と着地 (s=0) で 0。 */
function lateralRatio(path: IntroPath, s: number): number {
  if (path === 'line') return 0;
  if (path === 'arc') return Math.sin(Math.PI * (1 - s));
  // clothoid: 開始側で曲がり、着地に向けて偏差も傾きも 0 へ（真っ直ぐ締まって入る）。
  // s²(1−s) を最大値 4/27 で正規化する。
  return (s * s * (1 - s)) / (4 / 27);
}

/** ピース `piece` の時刻 `t` におけるオフセット（着地位置からのずれ。論理 px）。 */
export function introOffsetAt(plan: IntroPlan, piece: number, t: number): { x: number; y: number } {
  const p = plan.pieces[piece];
  if (p === undefined) return { x: 0, y: 0 };
  const s = remainingRatio(plan.motion, t, p.duration);
  const along = Math.hypot(p.dx, p.dy);
  if (along === 0 || s === 0) return { x: p.dx * s, y: p.dy * s };
  const lat = lateralRatio(plan.path, s) * p.bulge;
  const nx = p.dx / along;
  const ny = p.dy / along;
  return { x: p.dx * s + -ny * lat, y: p.dy * s + nx * lat };
}

/** 開始演出の計画を立てる。シードから決定的。 */
export function planIntro(seed: number, C: number, R: number, placement: Placement): IntroPlan {
  const N = C * R;
  const size = pieceSize(C, R);
  // シャッフル（core/shuffle）と乱数列を分けるため、シードを混ぜてから使う。
  const rand = mulberry32((seed ^ 0x9e3779b9) >>> 0);

  const angle = rand() * Math.PI * 2;
  const motion = (['linear', 'decel', 'adsorb'] as const)[
    Math.floor(rand() * 3) % 3
  ] as IntroMotion;
  const path = (['line', 'arc', 'clothoid'] as const)[Math.floor(rand() * 3) % 3] as IntroPath;
  // 膨らみの符号はステージ固定（全ピースが同じ側へ湾曲し、経路の交差を作らない）。
  const bulgeSign = rand() < 0.5 ? -1 : 1;
  const ux = Math.cos(angle);
  const uy = Math.sin(angle);

  // 着地位置（開始時の初期配置セル）。landing[piece] = 左上の論理座標。
  const landing = new Array<{ x: number; y: number }>(N);
  for (const [cell, piece] of placement.entries()) {
    landing[piece] = { x: col(cell, C) * size.w, y: row(cell, C) * size.h };
  }

  // 収まる順: 進入側から遠い（= 進入方向ベクトルとの内積が小さい）ピースが先。
  const order = Array.from({ length: N }, (_, piece) => piece).sort((a, b) => {
    const la = landing[a] as { x: number; y: number };
    const lb = landing[b] as { x: number; y: number };
    return (
      (la.x + size.w / 2) * ux +
      (la.y + size.h / 2) * uy -
      ((lb.x + size.w / 2) * ux + (lb.y + size.h / 2) * uy)
    );
  });

  const jitters = order.map(() => (rand() * 2 - 1) * ((INTRO_JITTER_DEG * Math.PI) / 180));
  const bulgeRatios = order.map(() => bulgeSign * (0.1 + rand() * 0.12));

  // 順位 1 段あたりの距離差。進入軸の投影がほぼ同じ 2 ピースが開始時から重なりにくい
  // 程度（対角の半分）は確保しつつ、距離比の下限 1.4 も満たす。重なりの最終保証は
  // 候補探索が行う。
  const step = Math.max(
    Math.hypot(size.w, size.h) * 0.5,
    (INTRO_BASE_DISTANCE * (INTRO_MIN_DISTANCE_RATIO - 1)) / Math.max(1, N - 1),
  );
  // 到着時刻は距離と独立に、狭い窓へ順位どおりに詰め込む（ざわざわっと連続着地）。
  const durationForRank = (rank: number): number =>
    INTRO_FIRST_ARRIVAL_MS + (INTRO_ARRIVAL_WINDOW_MS * rank) / Math.max(1, N - 1);

  const plans = new Array<IntroPiecePlan>(N);
  const fixed: number[] = [];
  // 探索中の暫定計画。introOffsetAt を計画と検査で共有するための器。
  const draft: IntroPiecePlan[] = new Array<IntroPiecePlan>(N);
  const probe: IntroPlan = {
    angle,
    motion,
    path,
    pieces: draft,
    total: INTRO_TOTAL_MS * 2,
    exit: { x: 0, y: 0 },
  };

  for (const [rank, piece] of order.entries()) {
    const baseDistance = INTRO_BASE_DISTANCE + step * rank;
    // 飛行時間は順位から決める（候補の距離ブーストでは変えない）。収まる順が
    // 厳密に順位どおりになり、ブーストされたピースは少し速く飛ぶだけになる。
    const duration = durationForRank(rank);
    const baseJitter = jitters[rank] as number;
    const baseBulge = (bulgeRatios[rank] as number) * baseDistance;

    // 候補（距離ブースト × ジッター縮小・反転 × 経路の直線化）を順に試し、
    // 確定済みピースと飛行中に重ならない最初のものを選ぶ。
    let picked: IntroPiecePlan | null = null;
    search: for (const boost of [1, 1.1, 1.2, 1.35, 1.5, 1.7, 1.9, 2.2]) {
      const distance = baseDistance * boost;
      for (const jitterScale of [1, 0.5, 0, -0.5, -1]) {
        for (const bulgeScale of [1, 0.5, 0]) {
          const a = angle + baseJitter * jitterScale;
          const candidate: IntroPiecePlan = {
            dx: Math.cos(a) * distance,
            dy: Math.sin(a) * distance,
            duration,
            bulge: baseBulge * bulgeScale,
          };
          draft[piece] = candidate;
          if (!collidesWithFixed(probe, landing, size, piece, fixed)) {
            picked = candidate;
            break search;
          }
        }
      }
    }
    // 一次候補が尽きたら、直線・ジッター無しで距離だけを大きく伸ばして退避させる
    // （速度が上がるだけで到着順は変わらない）。距離が伸びるほど飛行中は他ピースの
    // 後方に留まるため、有限回で必ず衝突が解ける。
    if (picked === null) {
      for (let boost = 2.6; boost <= 8; boost += 0.4) {
        const distance = baseDistance * boost;
        const candidate: IntroPiecePlan = {
          dx: Math.cos(angle) * distance,
          dy: Math.sin(angle) * distance,
          duration,
          bulge: 0,
        };
        draft[piece] = candidate;
        if (!collidesWithFixed(probe, landing, size, piece, fixed)) {
          picked = candidate;
          break;
        }
      }
    }
    const chosen: IntroPiecePlan = picked ?? {
      dx: Math.cos(angle) * baseDistance,
      dy: Math.sin(angle) * baseDistance,
      duration,
      bulge: 0,
    };
    draft[piece] = chosen;
    plans[piece] = chosen;
    fixed.push(piece);
  }

  return {
    angle,
    motion,
    path,
    pieces: plans,
    total: Math.max(...plans.map((p) => p.duration)),
    // 退場は進入側へ、盤面対角 + 余白ぶん動かして確実にフレームアウトさせる。
    exit: { x: ux * (INTRO_BASE_DISTANCE + 180), y: uy * (INTRO_BASE_DISTANCE + 180) },
  };
}

/** 候補ピース `piece` が確定済みピースのいずれかと飛行中に重なるか。 */
function collidesWithFixed(
  plan: IntroPlan,
  landing: readonly ({ x: number; y: number } | undefined)[],
  size: { w: number; h: number },
  piece: number,
  fixed: readonly number[],
): boolean {
  const self = landing[piece] as { x: number; y: number };
  for (let t = 0; t <= plan.total; t += SIM_STEP_MS) {
    const so = introOffsetAt(plan, piece, t);
    const sx = self.x + so.x;
    const sy = self.y + so.y;
    for (const other of fixed) {
      const land = landing[other] as { x: number; y: number };
      const oo = introOffsetAt(plan, other, t);
      if (
        Math.abs(sx - (land.x + oo.x)) < size.w - SIM_EPS &&
        Math.abs(sy - (land.y + oo.y)) < size.h - SIM_EPS
      ) {
        return true;
      }
    }
  }
  return false;
}

/** 計画の全時刻サンプルで重なりが無いかを数える（テスト・検算用）。0 が期待値。 */
export function countIntroOverlaps(
  plan: IntroPlan,
  C: number,
  R: number,
  placement: Placement,
): number {
  const size = pieceSize(C, R);
  const N = C * R;
  const landing = new Array<{ x: number; y: number }>(N);
  for (const [cell, piece] of placement.entries()) {
    landing[piece] = { x: col(cell, C) * size.w, y: row(cell, C) * size.h };
  }
  let overlaps = 0;
  const horizon = Math.max(plan.total, ...plan.pieces.map((p) => p.duration));
  for (let t = 0; t <= horizon; t += SIM_STEP_MS) {
    for (let a = 0; a < N; a++) {
      const la = landing[a] as { x: number; y: number };
      const oa = introOffsetAt(plan, a, t);
      for (let b = a + 1; b < N; b++) {
        const lb = landing[b] as { x: number; y: number };
        const ob = introOffsetAt(plan, b, t);
        if (
          Math.abs(la.x + oa.x - (lb.x + ob.x)) < size.w - SIM_EPS &&
          Math.abs(la.y + oa.y - (lb.y + ob.y)) < size.h - SIM_EPS
        ) {
          overlaps++;
        }
      }
    }
  }
  return overlaps;
}

/** 演出対象の最小面。`BoardView.pieceElement` が返す実 DOM がこれを満たす。 */
export interface IntroTarget {
  readonly style: {
    setProperty(name: string, value: string): void;
    removeProperty(name: string): void;
  };
}

/**
 * ピースを開始位置（画面外）へ置く。ページの初期描画前（view 構築直後）に呼ぶことで、
 * 年齢認証中も盤面には地だけが見える。
 */
export function applyIntroStart(
  plan: IntroPlan,
  pieceElement: (piece: number) => IntroTarget,
): void {
  for (const [piece] of plan.pieces.entries()) {
    const o = introOffsetAt(plan, piece, 0);
    const el = pieceElement(piece);
    el.style.setProperty('--fly-x', `${o.x}px`);
    el.style.setProperty('--fly-y', `${o.y}px`);
  }
}

/**
 * フライインを再生する。完了で resolve する。
 *
 * rAF 駆動で `introOffsetAt` をそのまま描画する（衝突検査済みの軌道と同一。CSS transition
 * では曲線経路と吸着が表現できない）。進行中は `board.dataset.intro = 'running'`、完了で
 * `'done'`（E2E と入力ゲートの観測点）。
 */
export function runIntro(
  plan: IntroPlan,
  board: HTMLElement,
  pieceElement: (piece: number) => HTMLElement,
  options: {
    readonly reducedMotion?: boolean;
    readonly raf?: (fn: () => void) => void;
    readonly now?: () => number;
  } = {},
): Promise<void> {
  const raf = options.raf ?? ((fn) => void requestAnimationFrame(() => fn()));
  const now = options.now ?? (() => performance.now());
  const finish = (): void => {
    for (const [piece] of plan.pieces.entries()) {
      const el = pieceElement(piece);
      el.style.removeProperty('--fly-x');
      el.style.removeProperty('--fly-y');
    }
    board.dataset.intro = 'done';
  };

  // 動きを減らす設定では演出そのものを省略し、即座に遊べる状態にする（NFR-006）。
  if (options.reducedMotion === true) {
    finish();
    return Promise.resolve();
  }

  board.dataset.intro = 'running';
  const start = now();
  return new Promise((resolve) => {
    const tick = (): void => {
      const t = now() - start;
      if (t >= plan.total) {
        finish();
        resolve();
        return;
      }
      for (const [piece] of plan.pieces.entries()) {
        const o = introOffsetAt(plan, piece, t);
        const el = pieceElement(piece);
        el.style.setProperty('--fly-x', `${o.x}px`);
        el.style.setProperty('--fly-y', `${o.y}px`);
      }
      raf(tick);
    };
    tick();
  });
}
